package org.teddapp.project.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import org.teddapp.project.server.AboutExtras
import org.teddapp.project.server.ArticleMeta
import org.teddapp.project.server.HomeExtras
import org.teddapp.project.server.LocalServerSession
import org.teddapp.project.server.ServerAccount
import org.teddapp.project.server.ServerApi
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerPublication
import org.teddapp.project.server.ServerRole
import org.teddapp.project.server.ServerSession
import org.teddapp.project.server.ServerSiteFile
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddConfirmDialog
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer
import org.teddapp.project.ui.settings.serverMessage
import org.teddapp.project.ui.notes.format
import org.teddapp.project.ui.teddPasswordTransformation
import org.teddapp.project.ui.teddSurface
import org.teddapp.project.ui.nav.TeddBottomNav
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding

enum class AdminOpenForm { None, Publish, Page }

/** Par où le back-office s'ouvre : les comptes, ou le site. Gardé pour les captures. */
enum class AdminTab { Accounts, Site }

/**
 * Les rubriques du back-office, rangées comme le site puis comme le serveur : d'abord
 * « Site public », puis « Système ». [sitePath] est la page du site qui leur répond.
 */
enum class SiteRubric(val label: String, val icon: String, val blurb: String, val sitePath: String? = null, val system: Boolean = false) {
    Home("Accueil", "home", "La première page du site : une en français, une en anglais. Son texte, ses chiffres et sa présentation de l'appli.", "/fr/"),
    Portfolio("Portfolio", "portfolio", "Les projets montrés sur le site. Une ligne par projet, une colonne par langue : l'état se lit d'un coup d'œil.", "/fr/portfolio/"),
    /** Le catalogue des articles : les notes publiées. */
    Blog("Blog", "notes", "Écris en privé, publie quand c'est prêt. Chaque article vit en français et en anglais, publiés séparément.", "/fr/blog/"),
    About("À propos", "users", "Qui tu es, comment te joindre. Une page par langue, et tes portraits dans Médias.", "/fr/a-propos/"),
    Other("Autres pages", "cards", "Les pages libres, avec leur propre adresse, hors des rubriques.", null),
    Media("Médias", "image", "Les portraits de la page À propos, et les images envoyées au serveur avec tes contenus.", null),
    Accounts("Comptes", "users", "Les comptes du serveur, leurs appareils et leur stockage.", null, system = true),
    Settings("Réglages", "settings", "Les adresses de téléchargement, les exemples, et les visites du site.", null, system = true),
}

/** État du back-office : ce que le serveur a rendu, et ce qu'on est en train de lui demander. */
internal class AdminModel(
    private val session: ServerSession,
    private val scope: CoroutineScope,
    private val repos: org.teddapp.project.db.TeddRepositories? = null,
) {
    /** La rubrique ouverte. En changer referme le formulaire et remet les filtres. */
    var rubric by mutableStateOf(SiteRubric.Home)
        private set
    var filter by mutableStateOf(RubricFilter.All)
    var search by mutableStateOf("")
    var accounts by mutableStateOf<List<ServerAccount>?>(null)
    var stats by mutableStateOf<org.teddapp.project.server.ServerStats?>(null)
    var publications by mutableStateOf<List<ServerPublication>?>(null)
    /** Adresses de téléchargement et portraits du site. */
    var site by mutableStateOf<org.teddapp.project.server.ServerSite?>(null)
    /** Les fichiers envoyés au serveur, pour Médias. */
    var files by mutableStateOf<List<ServerSiteFile>?>(null)
    var error by mutableStateOf<String?>(null)
    var busy by mutableStateOf(false)
    var creating by mutableStateOf(false)
    var formError by mutableStateOf<String?>(null)
    var quotaOf by mutableStateOf<ServerAccount?>(null)
    var confirmDelete by mutableStateOf<ServerAccount?>(null)
    var confirmRevoke by mutableStateOf<ServerAccount?>(null)
    var confirmUnpublish by mutableStateOf<ServerPublication?>(null)
    var confirmRemoveExamples by mutableStateOf(false)
    /** L'aperçu ouvert, s'il y en a un. */
    var preview by mutableStateOf<PreviewDoc?>(null)
    /** L'adresse du site : celle du serveur, sans son « api. ». */
    val siteUrl: String get() = session.url.trimEnd('/').replace("://api.", "://")
    /** Formulaire ouvert dans la rubrique, s'il y en a un. */
    var form by mutableStateOf<PublicationForm?>(null)
    /** Ce que l'appareil a de publiable : notes, pièces de portfolio, compétences. */
    var sources by mutableStateOf<List<LocalSource>>(emptyList())
    val snackbar = TeddSnackbarState()

    /** Tout est demandé d'un coup : l'écran se remplit dès que le serveur répond. */
    fun load() = run(onError = { error = it }) {
        error = null
        kotlinx.coroutines.coroutineScope {
            val a = async { session.authed { api, access -> api.accounts(access) } }
            val s = async { runCatching { session.authed { api, access -> api.stats(access) } }.getOrNull() }
            val p = async { session.authed { api, access -> api.publications(access) } }
            val w = async { session.authed { api, access -> api.site(access) } }
            val f = async { runCatching { session.authed { api, access -> api.siteFiles(access) } }.getOrNull() }
            publications = p.await()
            site = w.await()
            files = f.await()
            stats = s.await()
            accounts = a.await()
        }
    }

    /** Ce que compte une rubrique à côté de son nom : ses contenus en ligne, ses comptes, ses fichiers. */
    fun countOf(r: SiteRubric): Int? {
        val online = publications.orEmpty().filter { it.online }
        return when (r) {
            SiteRubric.Home -> online.count { it.isPage && it.role == "home" }
            SiteRubric.About -> online.count { it.isPage && it.role == "about" }
            SiteRubric.Portfolio -> online.count { it.source_kind == "portfolio_piece" }
            SiteRubric.Blog -> online.count { it.source_kind == "note" }
            SiteRubric.Other -> online.count { isOther(it) }
            SiteRubric.Media -> files.orEmpty().size + site?.photos.orEmpty().size
            SiteRubric.Accounts -> accounts?.size ?: 0
            SiteRubric.Settings -> 0
        }.takeIf { it > 0 }
    }

    fun create(email: String, password: String) = act("Compte créé", onError = { formError = it }) { api, access ->
        api.createAccount(access, email.trim(), password)
        creating = false
    }

    fun revoke(a: ServerAccount) = act("${a.email} est déconnecté de partout") { api, access ->
        api.revokeAccount(access, a.id)
        confirmRevoke = null
    }

    fun setQuota(a: ServerAccount, megabytes: Long) = act("Quota mis à jour", onError = { formError = it }) { api, access ->
        api.setQuota(access, a.id, megabytes * 1_048_576L)
        quotaOf = null
    }

    fun delete(a: ServerAccount) = act("Compte supprimé") { api, access ->
        api.deleteAccount(access, a.id)
        confirmDelete = null
    }

    fun unpublish(p: ServerPublication) = act("Retirée du site") { api, access ->
        api.unpublish(access, p.id)
        confirmUnpublish = null
    }

    fun openRubric(r: SiteRubric) {
        if (r != rubric) { form = null; filter = RubricFilter.All; search = "" }
        rubric = r
    }

    fun openPublish(preset: PublicationForm.Content = PublicationForm.Content()) {
        formError = null
        form = preset
        scope.launch { sources = loadLocalSources(repos) }
    }

    fun openNewPage(preset: PublicationForm.Page = PublicationForm.Page(null)) { formError = null; form = preset }

    /** L'aperçu d'une publication : son contenu n'est pas dans la liste, il faut le demander. */
    fun openPreview(p: ServerPublication) {
        // Déjà complète (contenu compris) : rien à demander.
        if (p.content != null) { preview = previewOf(p); return }
        scope.launch {
            try {
                preview = previewOf(session.authed { api, access -> api.publication(access, p.id) })
            } catch (e: ServerException) {
                snackbar.show(serverMessage(e))
            }
        }
    }

    /** L'aperçu d'un contenu de l'appareil, tel qu'il serait publié avec [doc] pour présentation. */
    fun previewSource(source: LocalSource, doc: PreviewDoc) {
        val db = repos?.db
        scope.launch {
            preview = kotlinx.coroutines.withContext(kotlinx.coroutines.Dispatchers.Default) {
                when (source.kind) {
                    "note" -> db?.noteQueries?.selectAll()?.executeAsList()?.firstOrNull { it.id == source.localId }?.let { n ->
                        doc.copy(title = n.title.orEmpty().ifBlank { "Sans titre" }, body = n.body, tags = n.tags.orEmpty().split(',').map { it.trim() }.filter { it.isNotEmpty() })
                    }
                    "portfolio_piece" -> db?.portfolioPieceQueries?.selectAll()?.executeAsList()?.firstOrNull { it.id == source.localId }?.let { p ->
                        doc.copy(
                            title = p.title, subtitle = p.subtitle.orEmpty(), body = p.body,
                            images = org.teddapp.project.portfolio.PortfolioCodecs.decodeImages(p.images).map { it.url to it.caption },
                            links = org.teddapp.project.portfolio.PortfolioCodecs.decodeLinks(p.links).map { it.label to it.url },
                        )
                    }
                    else -> null
                } ?: doc.copy(title = source.title)
            }
        }
    }

    /** Rouvre une page : son texte n'est pas dans la liste, il faut le demander. */
    fun openPage(p: ServerPublication) = run(onError = { snackbar.show(it) }) {
        formError = null
        form = PublicationForm.Page(session.authed { api, access -> api.publication(access, p.id) })
    }

    fun publish(source: LocalSource, slug: String, lang: String, translationOf: String?, article: ArticleMeta? = null) =
        act("Publié sur le site", onError = { formError = publishError(it) }) { api, access ->
            api.publish(access, source.kind, source.serverId, slug, lang, translationOf, article)
            form = null
        }

    /** Rouvre un article en ligne : sa présentation n'est pas dans la liste, il faut la demander. */
    fun openArticle(p: ServerPublication) = run(onError = { snackbar.show(it) }) {
        formError = null
        val full = session.authed { api, access -> api.publication(access, p.id) }
        form = PublicationForm.Content(kind = p.source_kind, lang = p.lang, lockKind = true, lockLang = true, existing = full)
    }

    /** Remet en ligne une publication telle qu'elle est : retirée, brouillon ou exemple. */
    fun republish(p: ServerPublication) = act("Remis en ligne", onError = { snackbar.show(publishError(it)) }) { api, access ->
        api.setOnline(access, p.id)
    }

    /** Ouvre l'éditeur sur une copie de [from] à traduire en [lang], reliée à l'original. */
    fun translate(from: ServerPublication, lang: String) = run(onError = { snackbar.show(it) }) {
        form = null
        val full = if (from.content != null) from else session.authed { api, access -> api.publication(access, from.id) }
        editor = EditorState.of(full, known()).copyForLang(lang, from.id)
    }

    fun savePage(
        id: String?, slug: String, lang: String, title: String, body: String, role: String?, translationOf: String?, published: Boolean,
        stats: List<Pair<String, String>> = emptyList(),
        home: HomeExtras? = null,
        about: AboutExtras? = null,
    ) =
        act(if (published) "Page en ligne" else "Brouillon enregistré", onError = { formError = it }) { api, access ->
            api.savePage(access, id, slug, lang, title, body, role, translationOf, published, stats, home, about)
            form = null
        }

    fun fillExamples() = run(onError = { snackbar.show(it) }) {
        val done = session.authed { api, access -> api.fillExamples(access) }
        publications = session.authed { api, access -> api.publications(access) }
        snackbar.show(
            when {
                done.count == 0 -> "Les exemples sont déjà là"
                done.skipped > 0 -> "${done.count} exemples en ligne ; tes pages déjà écrites gardent leur place"
                else -> "${done.count} exemples en ligne"
            },
        )
    }

    fun removeExamples() = run(onError = { snackbar.show(it) }) {
        confirmRemoveExamples = false
        val done = session.authed { api, access -> api.removeExamples(access) }
        publications = session.authed { api, access -> api.publications(access) }
        snackbar.show(if (done.count == 0) "Plus aucun exemple" else "${done.count} exemples retirés")
    }

    fun saveDownloads(downloads: Map<String, String>) = run(onError = { formError = it }) {
        formError = null
        site = session.authed { api, access -> api.saveSite(access, downloads) }
        snackbar.show("Adresses enregistrées")
    }

    /** Ouvre le sélecteur d'images de l'appareil, puis envoie la photo choisie. */
    fun choosePhoto(slot: Int) {
        val pick = TeddImagePicker.pick ?: return
        scope.launch {
            val image = runCatching { pick() }.getOrNull() ?: return@launch
            if (image.bytes.size > TeddImagePicker.MAX_BYTES) {
                snackbar.show("Cette photo est trop lourde (6 Mo au plus)")
                return@launch
            }
            run(onError = { snackbar.show(it) }) {
                session.authed { api, access -> api.uploadSitePhoto(access, slot, image.bytes, image.contentType) }
                site = session.authed { api, access -> api.site(access) }
                snackbar.show("Photo envoyée")
            }
        }
    }

    fun removePhoto(slot: Int) = run(onError = { snackbar.show(it) }) {
        session.authed { api, access -> api.deleteSitePhoto(access, slot) }
        site = session.authed { api, access -> api.site(access) }
        snackbar.show("Photo retirée")
    }

    // ── L'éditeur ──────────────────────────────────────────────────────

    /** Ce qu'on est en train d'écrire, s'il y a un éditeur ouvert. */
    var editor by mutableStateOf<EditorState?>(null)

    private fun known() = knownSeries(publications.orEmpty())

    /** Un contenu neuf dans l'éditeur : un article, une pièce, ou une page à sa place. */
    fun newDraft(kind: String, lang: String, role: String? = null, translationOf: String? = null) {
        form = null
        editor = EditorState(id = null, kind = kind, lang = lang, role = role, translationOf = translationOf, known = known()).also {
            if (role != null) { it.slug = when (role) { "home" -> if (lang == "en") "home" else "accueil"; else -> if (lang == "en") "about" else "a-propos" }; it.slugEdited = true }
        }
    }

    /** Rouvre une publication dans l'éditeur : son contenu n'est pas dans la liste, il faut le demander. */
    fun openEditor(p: ServerPublication) = run(onError = { snackbar.show(it) }) {
        form = null
        val full = if (p.content != null) p else session.authed { api, access -> api.publication(access, p.id) }
        editor = EditorState.of(full, known())
    }

    fun closeEditor() {
        editor = null
        scope.launch { runCatching { publications = session.authed { api, access -> api.publications(access) } } }
    }

    /** La même publication dans l'autre langue, si elle existe. */
    fun otherVersion(state: EditorState): ServerPublication? {
        val all = publications.orEmpty()
        state.translationOf?.let { of -> all.firstOrNull { it.id == of }?.let { return it } }
        val id = state.id ?: return null
        val key = all.firstOrNull { it.id == id }?.translation_key?.ifEmpty { id } ?: return null
        return all.firstOrNull { it.id != id && it.lang != state.lang && it.translation_key.ifEmpty { it.id } == key }
    }

    /** Passe à l'autre langue : la version qui existe s'ouvre ; sinon elle se crée, copiée de celle-ci. */
    fun switchLang(state: EditorState) {
        val other = otherVersion(state)
        if (other != null) { openEditor(other); return }
        val id = state.id ?: return
        editor = state.copyForLang(if (state.lang == "en") "fr" else "en", id)
    }

    /**
     * Enregistre ce qu'on écrit. [publish] null garde l'état (un brouillon reste un brouillon,
     * un contenu en ligne est mis à jour) ; vrai met en ligne ; faux retire du site.
     */
    fun saveEditor(publish: Boolean? = null) {
        val st = editor ?: return
        if (st.saving || !st.ready) return
        val published = publish ?: st.online
        st.saving = true
        st.error = null
        val fingerprint = st.fingerprint()
        scope.launch {
            try {
                val result = session.authed { api, access ->
                    if (st.isPage) api.savePage(
                        access, st.id, slugify(st.slug), st.lang, st.title.trim(), st.body.text.toString(), st.role,
                        st.translationOf.takeIf { st.id == null }, published,
                        stats = if (st.role == "home" || st.role == "about") parseStats(st.stats) else emptyList(),
                        home = if (st.role == "home") st.home.value() else null,
                        about = if (st.role == "about") st.about.value() else null,
                        presentation = st.presentation(),
                    ) else api.saveDraft(access, st.draft(published).copy(translationOf = st.translationOf.takeIf { st.id == null }))
                }
                st.id = result.id
                st.online = published
                st.savedFingerprint = fingerprint
                st.savedAtMs = org.teddapp.project.db.nowMillis()
                if (publish == true) snackbar.show("En ligne sur le site, dans une minute")
                else if (publish == false) snackbar.show("Retiré du site")
                runCatching { publications = session.authed { api, access -> api.publications(access) } }
            } catch (e: ServerException) {
                st.error = publishError(serverMessage(e))
                if (e.code == "forbidden") runCatching { session.refreshRole() }
            } finally {
                st.saving = false
            }
        }
    }

    fun deleteEditor() {
        val st = editor ?: return
        st.confirmDelete = false
        val id = st.id ?: run { editor = null; return }
        run(onError = { st.error = it }) {
            session.authed { api, access -> api.deleteDraft(access, id) }
            editor = null
            publications = session.authed { api, access -> api.publications(access) }
            snackbar.show("Effacé du serveur")
        }
    }

    /** Envoie une image choisie sur l'appareil ; rend `file:<id>`, ou null si l'on a renoncé. */
    private suspend fun uploadPicked(): String? {
        val pick = TeddImagePicker.pick ?: run { snackbar.show("Pas de sélecteur d'images sur cet appareil."); return null }
        val image = runCatching { pick() }.getOrNull() ?: return null
        if (image.bytes.size > TeddImagePicker.MAX_BYTES) { snackbar.show("Cette image est trop lourde (6 Mo au plus)"); return null }
        val name = "image." + when (image.contentType) { "image/png" -> "png"; "image/webp" -> "webp"; else -> "jpg" }
        return try {
            "file:" + session.authed { api, access -> api.uploadSiteImage(access, image.bytes, image.contentType, name) }
        } catch (e: ServerException) {
            snackbar.show(serverMessage(e)); null
        }
    }

    suspend fun pickCover(state: EditorState) { uploadPicked()?.let { state.cover = it; refreshFiles() } }

    suspend fun pickPieceImage(state: EditorState) { uploadPicked()?.let { state.images.add(it to ""); refreshFiles() } }

    /** La galerie ouverte pour choisir une image ; ce qu'on en fait est dans [onPick]. */
    var galleryPicker by mutableStateOf<((String) -> Unit)?>(null)

    /** Pose une image de l'appareil au curseur du texte, envoyée au serveur d'abord. */
    suspend fun pickImageInto(state: EditorState, body: androidx.compose.foundation.text.input.TextFieldState) {
        val ref = uploadPicked() ?: return
        body.format { t, s, _ -> org.teddapp.project.ui.notes.MarkdownEdits.block(t, s, "![](" + ref + ")") }
        refreshFiles()
    }

    /** Les octets d'une image déposée sur le serveur, pour la montrer avant qu'elle soit en ligne. */
    suspend fun imageBytes(id: String): ByteArray? =
        runCatching { session.authed { api, access -> api.siteImage(access, id) } }.getOrNull()

    /** Les octets d'un fichier de la galerie : une image de l'éditeur, ou un fichier joint par l'appareil. */
    suspend fun galleryBytes(f: ServerSiteFile): ByteArray? = runCatching {
        if (f.owner_kind == "site") session.authed { api, access -> api.siteImage(access, f.id) }
        else session.authed { api, access -> api.getSigned(api.fileDownloadUrl(access, f.id).url) }
    }.getOrNull()

    /** Rafraîchit la galerie : après un envoi, d'où qu'il vienne. */
    fun refreshFiles() { scope.launch { runCatching { session.authed { api, access -> api.siteFiles(access) } }.onSuccess { files = it } } }

    /** Ajoute une image à la galerie depuis l'appareil. */
    fun addToGallery() { scope.launch { uploadPicked()?.let { refreshFiles(); snackbar.show("Image ajoutée à la galerie") } } }

    fun deleteFromGallery(f: ServerSiteFile) = run(onError = { snackbar.show(it) }) {
        session.authed { api, access -> api.deleteSiteImage(access, f.id) }
        files = session.authed { api, access -> api.siteFiles(access) }
        snackbar.show("Image supprimée")
    }

    /** Met un article ou une pièce sur l'accueil, ou l'en retire. */
    fun setFeatured(p: ServerPublication, featured: Boolean) =
        act(if (featured) "Sur l'accueil du site" else "Retiré de l'accueil") { api, access -> api.feature(access, p.id, featured) }

    /** Les contenus de l'accueil d'un type, dans l'ordre choisi. */
    fun featured(kind: String): List<ServerPublication> =
        publications.orEmpty().filter { it.source_kind == kind && it.featured }.sortedWith(compareBy({ it.featured_rank ?: Long.MAX_VALUE }, { -it.published_at_ms }))

    /** Déplace un contenu de l'accueil d'une place, puis renumérote tout le monde. */
    fun moveFeatured(p: ServerPublication, delta: Int) {
        val list = featured(p.source_kind).toMutableList()
        val at = list.indexOfFirst { it.id == p.id }
        val to = (at + delta).coerceIn(0, list.lastIndex)
        if (at < 0 || to == at) return
        list.add(to, list.removeAt(at))
        run(onError = { snackbar.show(it) }) {
            session.authed { api, access -> list.forEachIndexed { i, q -> api.feature(access, q.id, true, i + 1) } }
            publications = session.authed { api, access -> api.publications(access) }
        }
    }

    /** Une action, puis la liste relue : ce qu'on affiche est toujours ce que le serveur a. */
    private fun act(done: String, onError: (String) -> Unit = { snackbar.show(it) }, block: suspend (ServerApi, String) -> Unit) =
        run(onError) {
            formError = null
            session.authed { api, access -> block(api, access) }
            accounts = session.authed { api, access -> api.accounts(access) }
            publications = session.authed { api, access -> api.publications(access) }
            snackbar.show(done)
        }

    private fun run(onError: (String) -> Unit, block: suspend () -> Unit) {
        if (busy) return
        busy = true
        scope.launch {
            try {
                block()
            } catch (e: ServerException) {
                onError(if (e.code == "not_found") NOT_FOUND else serverMessage(e))
                // Le rôle a pu être retiré entre-temps : l'interface doit le savoir.
                if (e.code == "forbidden") runCatching { session.refreshRole() }
            } finally {
                busy = false
            }
        }
    }
}

/** Ni page d'accueil, ni page À propos, ni portfolio, ni article : pages libres, compétences, et ce que le site ne range nulle part. */
internal fun isOther(p: ServerPublication) =
    if (p.isPage) p.role == null else p.source_kind != "note" && p.source_kind != "portfolio_piece"

/**
 * Back-office du site, réservé au `supra_user` : les comptes et ce qui est publié.
 * Tout se passe sur le serveur ; cet écran ne garde rien en local. En fenêtre large,
 * une seconde barre latérale range les rubriques ; sur téléphone, une rangée de puces.
 */
@Composable
fun AdminScreen(
    onNavigate: (Section) -> Unit = {},
    /** Les données de l'appareil : ce qu'on peut publier. Absent, seules les pages se rédigent. */
    repos: org.teddapp.project.db.TeddRepositories? = null,
    modifier: Modifier = Modifier,
    initialTab: AdminTab = AdminTab.Site,
    initialRubric: SiteRubric = SiteRubric.Home,
    /** Formulaire ouvert d'entrée, pour les captures. */
    initialForm: AdminOpenForm = AdminOpenForm.None,
    /** Une publication complète dont l'aperçu est ouvert d'entrée, pour les captures. */
    initialPreview: ServerPublication? = null,
    /** Une publication complète ouverte dans l'éditeur d'entrée, pour les captures. */
    initialEditor: ServerPublication? = null,
    /** Avec [initialEditor] : le dialogue Publier ouvert d'entrée, pour les captures. */
    initialPublishDialog: Boolean = false,
) {
    val session = LocalServerSession.current
    val drawer = rememberSettingsDrawer()
    val supra = session != null && session.isSupra
    val scope = rememberCoroutineScope()
    val model = if (session != null && supra) remember(session) {
        AdminModel(session, scope, repos).also {
            it.openRubric(if (initialTab == AdminTab.Accounts) SiteRubric.Accounts else initialRubric)
            when (initialForm) {
                AdminOpenForm.Publish -> it.openPublish(
                    PublicationForm.Content(kind = if (initialRubric == SiteRubric.Portfolio) "portfolio_piece" else "note", lockKind = true),
                )
                AdminOpenForm.Page -> it.openNewPage(
                    when (initialRubric) {
                        SiteRubric.Home -> PublicationForm.Page(null, role = "home", lockPlace = true)
                        SiteRubric.About -> PublicationForm.Page(null, role = "about", lockPlace = true)
                        else -> PublicationForm.Page(null)
                    },
                )
                AdminOpenForm.None -> Unit
            }
            initialPreview?.let(it::openPreview)
            initialEditor?.let { p -> it.openEditor(p); if (initialPublishDialog) it.editor?.confirmPublish = true }
        }
    } else null
    if (model != null) LaunchedEffect(model) { model.load() }

    // La taille de fenêtre n'est connue qu'à l'intérieur de WithWindowClass : elle est passée au corps.
    val body: @Composable (Modifier, Boolean) -> Unit = { bodyModifier, compact ->
        Box(modifier = bodyModifier) {
            if (session == null || model == null) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    TeddEmptyState(
                        title = "Réservé au supra_user",
                        message = "Connecte-toi au serveur avec le compte qui tient le site, dans les Réglages.",
                        icon = "lock",
                        actionLabel = if (compact) null else "Ouvrir les réglages",
                        onAction = if (compact) null else drawer::open,
                    )
                }
            } else {
                AdminContent(model, session, compact)
                TeddSnackbarHost(model.snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 28.dp))
            }
        }
    }
    org.teddapp.project.ui.adaptive.WithWindowClass(modifier.fillMaxSize()) { wc ->
        if (wc == org.teddapp.project.ui.adaptive.WindowClass.Compact) {
            Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg).statusBarsPadding()) {
                body(Modifier.weight(1f).fillMaxWidth(), true)
                TeddBottomNav(active = Section.Admin, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
            }
        } else {
            SettingsDrawerHost(drawer, onNavigate = onNavigate) {
                Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
                    TeddNavRail(active = Section.Admin, onNavigate = onNavigate, onSettingsClick = drawer::open)
                    if (model != null && model.editor == null) AdminSidebar(model)
                    body(Modifier.weight(1f).fillMaxSize(), false)
                }
            }
        }
    }
}

@Composable
private fun AdminContent(model: AdminModel, session: ServerSession, compact: Boolean) {
    androidx.compose.runtime.CompositionLocalProvider(LocalSiteImageLoader provides { id -> model.imageBytes(id) }) {
        val editor = model.editor
        if (editor != null) EditorScreen(model, editor, compact) else RubricContent(model, session, compact)
        AdminDialogs(model)
        PreviewDialog(model)
        GalleryPickerDialog(model)
    }
}

@Composable
private fun RubricContent(model: AdminModel, session: ServerSession, compact: Boolean) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = if (compact) TeddSpace.S4 else TeddSpace.S8, vertical = if (compact) TeddSpace.S4 else TeddSpace.S6),
        verticalArrangement = Arrangement.spacedBy(if (compact) TeddSpace.S4 else TeddSpace.S5),
    ) {
        if (compact) {
            Text("BACK-OFFICE · " + session.url.removePrefix("https://").removePrefix("http://").uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline, maxLines = 1, overflow = TextOverflow.Ellipsis)
            RubricChips(model)
        }
        AdminBody(model, session, compact)
    }
}

@Composable
private fun AdminBody(model: AdminModel, session: ServerSession, compact: Boolean) {
    val error = model.error
    when {
        error != null -> TeddEmptyState(
            title = "Le serveur n'a pas répondu comme prévu",
            message = error,
            icon = "alert",
            actionLabel = "Réessayer",
            onAction = { model.load() },
            modifier = Modifier.padding(top = TeddSpace.S8),
        )
        model.accounts == null -> {
            RubricHeader(model, compact)
            Text("Chargement…", color = TeddTokens.InkMuted, style = TeddType.Body)
        }
        model.rubric == SiteRubric.Accounts -> AccountsPanel(model, session, compact)
        else -> SiteTab(model, compact)
    }
}

@Composable
private fun AdminDialogs(model: AdminModel) {
    model.confirmDelete?.let { a ->
        TeddConfirmDialog(
            title = "Supprimer ce compte ?",
            message = "${a.email} et toutes ses données seront supprimés du serveur. C'est définitif.",
            confirmLabel = "Supprimer le compte",
            onConfirm = { model.delete(a) },
            onDismiss = { model.confirmDelete = null },
        )
    }
    model.confirmRevoke?.let { a ->
        TeddConfirmDialog(
            title = "Déconnecter de partout ?",
            message = "Tous les appareils de ${a.email} devront se reconnecter avec le mot de passe. Les données restent.",
            confirmLabel = "Déconnecter",
            onConfirm = { model.revoke(a) },
            onDismiss = { model.confirmRevoke = null },
            danger = false,
        )
    }
    if (model.confirmRemoveExamples) {
        val count = model.publications.orEmpty().count { it.example }
        TeddConfirmDialog(
            title = "Retirer les exemples ?",
            message = "Les $count exemples encore présents seront effacés du serveur. Ce que tu as réécrit reste.",
            confirmLabel = "Retirer les exemples",
            onConfirm = { model.removeExamples() },
            onDismiss = { model.confirmRemoveExamples = false },
        )
    }
    model.confirmUnpublish?.let { p ->
        TeddConfirmDialog(
            title = "Retirer du site ?",
            message = "« ${p.title} » ne sera plus lisible sur le site. La copie publiée est conservée.",
            confirmLabel = "Retirer du site",
            onConfirm = { model.unpublish(p) },
            onDismiss = { model.confirmUnpublish = null },
            danger = false,
        )
    }
}

// ── Comptes ────────────────────────────────────────────────────────────

@Composable
private fun AccountsPanel(model: AdminModel, session: ServerSession, compact: Boolean) {
    val accounts = model.accounts.orEmpty()
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
        RubricHeader(model, compact) {
            if (!model.creating) {
                TeddButton(text = "Nouveau compte", onClick = { model.creating = true; model.formError = null; model.quotaOf = null }, kind = TeddButtonKind.Primary, icon = "plus")
            }
        }
        model.stats?.let { ActivityTiles(it, compact) }
        if (model.creating) NewAccountForm(model)
        model.quotaOf?.let { QuotaForm(model, it) }
        if (compact) {
            Column(modifier = Modifier.fillMaxWidth().teddSurface(RoundedCornerShape(TeddTokens.RMd))) {
                accounts.forEachIndexed { index, account ->
                    if (index > 0) Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
                    AccountRow(account, isSelf = account.email == session.email, model = model)
                }
            }
        } else {
            AdminTable(
                columns = listOf(
                    TableColumn("Compte", 1f),
                    TableColumn("Appareils", width = 88.dp),
                    TableColumn("Stockage", width = 150.dp),
                    TableColumn("Dernière activité", width = 160.dp),
                    TableColumn("", width = 40.dp),
                ),
                rows = accounts.map { a ->
                    val isSelf = a.email == session.email
                    listOf(
                        {
                            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                                    Text(a.email, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                    if (a.role == ServerRole.SUPRA) TeddTag("supra_user", tone = TeddTagTone.Public, icon = "globe")
                                    if (isSelf) TeddTag("toi", tone = TeddTagTone.Outline)
                                }
                                Text("créé le ${formatDay(a.created_at_ms)}", color = TeddTokens.InkMuted, style = TeddType.Caption)
                            }
                        },
                        { MonoCell("${a.devices}") },
                        { MonoCell("${formatBytes(a.storage_used_bytes)} sur ${formatBytes(a.storage_limit_bytes)}") },
                        { Text(lastActivity(a.last_seen_ms), color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis) },
                        { AccountMenu(a, isSelf, model) },
                    )
                },
            )
        }
    }
}

/**
 * L'activité en quatre chiffres : en ligne maintenant, actifs aujourd'hui, sur 7 jours,
 * sur 30 jours. Un compte est actif quand un de ses appareils s'est synchronisé.
 */
@Composable
private fun ActivityTiles(stats: org.teddapp.project.server.ServerStats, compact: Boolean) {
    val tiles = listOf(
        Triple("EN LIGNE", stats.online_now, "application ouverte maintenant"),
        Triple("AUJOURD'HUI", stats.active_today, "sur ${stats.accounts} compte${if (stats.accounts > 1) "s" else ""}"),
        Triple("7 JOURS", stats.active_7d, "comptes actifs"),
        Triple("30 JOURS", stats.active_30d, "comptes actifs"),
    )
    val byPlatform = stats.devices.joinToString(" · ") { "${it.devices} ${platformLabel(it.platform)}" }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        androidx.compose.foundation.layout.FlowRow(
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            maxItemsInEachRow = if (compact) 2 else 4,
        ) {
            tiles.forEach { (label, value, hint) ->
                Column(
                    modifier = Modifier.weight(1f).teddSurface(RoundedCornerShape(TeddTokens.RMd)).padding(TeddSpace.S4),
                    verticalArrangement = Arrangement.spacedBy(2.dp),
                ) {
                    Text(label, color = TeddTokens.InkMuted, style = TeddType.Overline)
                    Text(value.toString(), color = TeddTokens.Ink, style = TeddType.MonoSm.copy(fontSize = 26.sp))
                    Text(hint, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
            }
        }
        if (byPlatform.isNotEmpty()) Text("Appareils : $byPlatform", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
        // Les comptes créés par semaine : une barre par semaine, la plus ancienne à gauche.
        if (stats.signups_by_week.isNotEmpty()) {
            val max = stats.signups_by_week.maxOf { it.accounts }.coerceAtLeast(1)
            Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                Text("COMPTES CRÉÉS · 8 SEMAINES", color = TeddTokens.InkMuted, style = TeddType.Overline)
                Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(3.dp)) {
                    stats.signups_by_week.forEach { w ->
                        Box(
                            Modifier.width(14.dp).height((4 + 20 * w.accounts / max).toInt().dp)
                                .background(if (w.accounts > 0) TeddTokens.LimeStrong else TeddTokens.Line, RoundedCornerShape(2.dp)),
                        )
                    }
                }
                Text(stats.signups_by_week.joinToString(" · ") { "${it.accounts}" }, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

private fun platformLabel(platform: String) = when (platform) {
    "desktop" -> "ordinateur"
    "android" -> "téléphone"
    "wear" -> "montre"
    "web" -> "navigateur"
    "ios" -> "iPhone"
    else -> platform
}

/** « actif il y a 3 min », « actif hier », « jamais synchronisé ». */
private fun lastActivity(lastSeenMs: Long?): String {
    if (lastSeenMs == null) return "jamais synchronisé"
    val ago = org.teddapp.project.db.nowMillis() - lastSeenMs
    val minutes = ago / 60_000L
    return when {
        minutes < 2 -> "actif à l'instant"
        minutes < 60 -> "actif il y a $minutes min"
        minutes < 60 * 24 -> "actif il y a ${minutes / 60} h"
        minutes < 60 * 48 -> "actif hier"
        else -> "actif il y a ${minutes / (60 * 24)} jours"
    }
}

/** Le menu « … » d'un compte. Le serveur refuse de supprimer un supra_user ou soi-même : on ne le propose pas. */
@Composable
private fun AccountMenu(a: ServerAccount, isSelf: Boolean, model: AdminModel) {
    val supra = a.role == ServerRole.SUPRA
    ProjectMoreMenu(
        listOfNotNull(
            MenuAction("Changer le quota", "edit", { model.quotaOf = a; model.creating = false; model.formError = null }),
            MenuAction("Déconnecter de partout", "lock", { model.confirmRevoke = a }),
            if (!supra && !isSelf) MenuAction("Supprimer le compte", "trash", { model.confirmDelete = a }, danger = true) else null,
        ),
    )
}

/** Sur téléphone, un compte par ligne : l'adresse, puis ses chiffres en dessous. */
@Composable
private fun AccountRow(a: ServerAccount, isSelf: Boolean, model: AdminModel) {
    val supra = a.role == ServerRole.SUPRA
    Row(
        modifier = Modifier.fillMaxWidth().padding(start = TeddSpace.S4, end = TeddSpace.S2, top = TeddSpace.S3, bottom = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4),
    ) {
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text(a.email, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                if (supra) TeddTag("supra_user", tone = TeddTagTone.Public, icon = "globe")
                if (isSelf) TeddTag("toi", tone = TeddTagTone.Outline)
            }
            Text(
                text = "créé le ${formatDay(a.created_at_ms)} · ${a.devices} appareil${if (a.devices > 1) "s" else ""} · ${lastActivity(a.last_seen_ms)}",
                color = TeddTokens.InkMuted,
                style = TeddType.Caption,
            )
            Text("${formatBytes(a.storage_used_bytes)} sur ${formatBytes(a.storage_limit_bytes)}", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
        }
        AccountMenu(a, isSelf, model)
    }
}

@Composable
internal fun FormError(message: String?) {
    if (message == null) return
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Danger)
        Text(message, color = TeddTokens.Danger, style = TeddType.Caption)
    }
}

@Composable
private fun NewAccountForm(model: AdminModel) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    TeddCard(modifier = Modifier.widthIn(max = 520.dp), title = "Nouveau compte", icon = "users", padding = PaddingValues(TeddSpace.S5)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Text(
                "Un compte ordinaire. Transmets-lui son mot de passe : il pourra le changer une fois connecté.",
                color = TeddTokens.InkMuted, style = TeddType.Caption,
            )
            TeddField(label = "E-mail", value = email, onValueChange = { email = it }, placeholder = "prenom@exemple.org")
            TeddField(label = "Mot de passe", value = password, onValueChange = { password = it }, visualTransformation = teddPasswordTransformation())
            FormError(model.formError)
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(
                    text = if (model.busy) "Création…" else "Créer le compte",
                    onClick = { model.create(email, password) },
                    kind = TeddButtonKind.Primary,
                    enabled = !model.busy && email.contains('@') && password.isNotEmpty(),
                )
                TeddButton(text = "Annuler", onClick = { model.creating = false }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

@Composable
private fun QuotaForm(model: AdminModel, a: ServerAccount) {
    var megabytes by remember(a.id) { mutableStateOf((a.storage_limit_bytes / 1_048_576L).toString()) }
    val value = megabytes.trim().toLongOrNull()
    TeddCard(modifier = Modifier.widthIn(max = 520.dp), title = "Quota de ${a.email}", icon = "edit", padding = PaddingValues(TeddSpace.S5)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Text("Utilisé : ${formatBytes(a.storage_used_bytes)}.", color = TeddTokens.InkMuted, style = TeddType.Caption)
            TeddField(label = "Limite de stockage, en Mo", value = megabytes, onValueChange = { megabytes = it.filter(Char::isDigit) })
            FormError(model.formError)
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(
                    text = "Enregistrer",
                    onClick = { value?.let { model.setQuota(a, it) } },
                    kind = TeddButtonKind.Primary,
                    enabled = !model.busy && value != null,
                )
                TeddButton(text = "Annuler", onClick = { model.quotaOf = null }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

// ── Formats ────────────────────────────────────────────────────────────

internal fun formatDay(epochMs: Long): String {
    val iso = TeddDates.isoFromDays((epochMs / 86_400_000L).toInt())
    return TeddDates.formatShort(iso, withYear = true) ?: iso
}

/** « 512 Mo », « 1,5 Go » : l'unité qui se lit, une décimale au plus. */
internal fun formatBytes(bytes: Long): String {
    val units = listOf("o", "Ko", "Mo", "Go", "To")
    var value = bytes.toDouble()
    var unit = 0
    while (value >= 1024 && unit < units.lastIndex) { value /= 1024; unit++ }
    val tenths = kotlin.math.round(value * 10).toLong()
    val text = if (tenths % 10 == 0L) "${tenths / 10}" else "${tenths / 10},${tenths % 10}"
    return "$text ${units[unit]}"
}
